import React, {createContext, useContext, useEffect, useState} from 'react';
import {useVideo} from './VideoContext';
import {API_CONFIG} from '../config/apiConfig';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

/**
 * Empreinte du déploiement courant, lue sur le bundle de l'application.
 *
 * Les feuilles de thème gardent le même nom d'un déploiement à l'autre :
 * `assets/css/halloween-theme.css` aujourd'hui comme il y a six mois. Le
 * navigateur les garde donc en cache et continue de servir l'ancienne
 * version indéfiniment — au point de faire juger un thème sur un fichier
 * périmé, ce qui est exactement arrivé.
 *
 * Le nom du bundle, lui, porte un hachage qui change à chaque build. En
 * l'accrochant à l'URL du thème, chaque déploiement invalide le cache, et
 * lui seul : deux visites sur la même version continuent de le réutiliser.
 */
const buildTag = () => {
    const script = document.querySelector('script[src*="/static/js/main."]');
    const match = script?.src?.match(/main\.([a-z0-9]+)\.js/);
    return match ? match[1] : 'dev';
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState('default');
    const [loading, setLoading] = useState(true);

    // 🎬 Récupérer l'état de la vidéo
    const { isVideoPlaying, isVideoFullscreen } = useVideo();

    useEffect(() => {
        fetchTheme();

        // Sondage toutes les 5 minutes, suspendu quand l'onglet est masqué :
        // un thème saisonnier ne change pas toutes les 30 secondes.
        const tick = () => { if (!document.hidden) fetchTheme(); };
        const interval = setInterval(tick, 300000);
        document.addEventListener('visibilitychange', tick);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', tick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 🎬 EFFET PRINCIPAL : Gérer la visibilité quand la vidéo change d'état
    useEffect(() => {
        // Masquer si : vidéo EN LECTURE ET PAS en fullscreen
        const shouldHide = isVideoPlaying;

        const snowflakes = document.getElementById('snowflakes-container');
        const bats = document.getElementById('bats-container');
        const webs = document.getElementById('spider-webs');
        const fog = document.getElementById('halloween-fog');
        const spider = document.getElementById('halloween-spider');
        const pumpkins = document.getElementById('pumpkins-container');

        // L'araignée est en `flex` et non `block` : son fil et son corps
        // s'empilent, elle disparaîtrait de travers avec le mauvais display.
        if (spider) spider.style.display = shouldHide ? 'none' : 'flex';
        if (pumpkins) pumpkins.style.display = shouldHide ? 'none' : 'block';

        if (snowflakes) {
            snowflakes.style.display = shouldHide ? 'none' : 'block';
        }
        if (bats) {
            bats.style.display = shouldHide ? 'none' : 'block';
        }
        if (webs) {
            webs.style.display = shouldHide ? 'none' : 'block';
        }
        if (fog) {
            fog.style.display = shouldHide ? 'none' : 'block';
        }

    }, [isVideoPlaying, isVideoFullscreen]);

    const fetchTheme = async () => {
        try {
            const response = await fetch(`${API_CONFIG.NEW_API_BASE}/theme`);
            const data = await response.json();

            if (data.success) {
                const newTheme = data.theme || 'default';

                // Si le thème a changé, recharger la page
                if (theme !== 'default' && newTheme !== theme && !loading) {
                    window.location.reload();
                } else {
                    setTheme(newTheme);
                    applyTheme(newTheme);
                }
            }
        } catch (error) {
            // Silencieux : le thème est décoratif, son échec n'a aucune
            // conséquence fonctionnelle et polluait la console en boucle.
            setTheme('default');
        }
        setLoading(false);
    };

    const applyTheme = (themeName) => {
        // Retirer tous les thèmes précédents
        document.documentElement.classList.remove('theme-default', 'theme-christmas', 'theme-halloween');

        // Appliquer le nouveau thème
        document.documentElement.classList.add(`theme-${themeName}`);

        // Charger le CSS du thème
        if (themeName === 'christmas') {
            loadChristmasTheme();
            removeHalloweenTheme();
        } else if (themeName === 'halloween') {
            loadHalloweenTheme();
            removeChristmasTheme();
        } else {
            removeChristmasTheme();
            removeHalloweenTheme();
        }
    };

    // ============================================
    // THÈME DE NOËL
    // ============================================
    const loadChristmasTheme = () => {
        // Charger le CSS
        if (!document.getElementById('christmas-theme-css')) {
            const link = document.createElement('link');
            link.id = 'christmas-theme-css';
            link.rel = 'stylesheet';
            link.href = '/assets/css/christmas-theme.css?v=' + buildTag();
            document.head.appendChild(link);
        }

        // Ajouter les flocons
        if (!document.getElementById('snowflakes-container')) {
            const snowContainer = document.createElement('div');
            snowContainer.id = 'snowflakes-container';
            snowContainer.className = 'snowflakes';
            snowContainer.setAttribute('aria-hidden', 'true');

            // Le JS ne décide que du hasard, jamais de l'apparence : il pose
            // des propriétés personnalisées et la feuille du thème s'occupe du
            // reste. L'ancienne version écrivait des styles en ligne (taille,
            // opacité) que le CSS ne pouvait plus contredire, et faisait tomber
            // un emoji ❄ en deux secondes — frénétique, et illisible sur une
            // affiche.
            for (let i = 0; i < 40; i++) {
                const snowflake = document.createElement('div');
                snowflake.className = 'snowflake';
                snowflake.style.setProperty('--x', Math.random() * 100 + '%');
                snowflake.style.setProperty('--drift', (Math.random() * 120 - 60) + 'px');
                snowflake.style.setProperty('--delay', -Math.random() * 20 + 's');
                snowflake.style.setProperty('--duration', (Math.random() * 12 + 14) + 's');
                snowflake.style.setProperty('--scale', (Math.random() * 0.8 + 0.6).toFixed(2));
                snowContainer.appendChild(snowflake);
            }

            document.body.appendChild(snowContainer);
             // ou (isVideoPlaying && !isVideoFullscreen) si tu gardes l’ancienne logique
            snowContainer.style.display = isVideoPlaying ? 'none' : 'block';
            console.log('❄️ Flocons créés');
        }
    };

    const removeChristmasTheme = () => {
        const css = document.getElementById('christmas-theme-css');
        if (css) css.remove();

        const snowContainer = document.getElementById('snowflakes-container');
        if (snowContainer) snowContainer.remove();
    };

    // ============================================
    // THÈME HALLOWEEN
    // ============================================
    const loadHalloweenTheme = () => {
        // Charger le CSS
        if (!document.getElementById('halloween-theme-css')) {
            const link = document.createElement('link');
            link.id = 'halloween-theme-css';
            link.rel = 'stylesheet';
            link.href = '/assets/css/halloween-theme.css?v=' + buildTag();
            document.head.appendChild(link);
        }

        // Ajouter les chauves-souris
        if (!document.getElementById('bats-container')) {
            const batsContainer = document.createElement('div');
            batsContainer.id = 'bats-container';
            batsContainer.className = 'bats-container';
            batsContainer.setAttribute('aria-hidden', 'true');

            // Six suffisent : une nuée de quinze emojis traversait l'écran en
            // permanence. Même principe que la neige — le JS tire au sort, le
            // CSS dessine.
            for (let i = 0; i < 6; i++) {
                const bat = document.createElement('div');
                bat.className = 'bat';
                bat.style.setProperty('--y', (Math.random() * 55 + 5) + '%');
                bat.style.setProperty('--delay', -Math.random() * 40 + 's');
                bat.style.setProperty('--duration', (Math.random() * 14 + 22) + 's');
                bat.style.setProperty('--scale', (Math.random() * 0.5 + 0.7).toFixed(2));
                batsContainer.appendChild(bat);
            }

            document.body.appendChild(batsContainer);

            batsContainer.style.display = isVideoPlaying ? 'none' : 'block';
        }

        // Ajouter les toiles d'araignée
        if (!document.getElementById('spider-webs')) {
            const websContainer = document.createElement('div');
            websContainer.id = 'spider-webs';
            websContainer.setAttribute('aria-hidden', 'true');

            // Toile en haut à gauche
            const webTopLeft = document.createElement('div');
            webTopLeft.className = 'spider-web top-left';
            webTopLeft.innerHTML = `
                <svg width="200" height="200" viewBox="0 0 200 200">
                    <path d="M 0,0 L 100,100" stroke="rgba(255,255,255,0.3)" stroke-width="1"/>
                    <path d="M 200,0 L 100,100" stroke="rgba(255,255,255,0.3)" stroke-width="1"/>
                    <path d="M 0,200 L 100,100" stroke="rgba(255,255,255,0.3)" stroke-width="1"/>
                    <circle cx="100" cy="100" r="20" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="1"/>
                    <circle cx="100" cy="100" r="40" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="1"/>
                    <circle cx="100" cy="100" r="60" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="1"/>
                </svg>
            `;

            // Toile en haut à droite
            const webTopRight = document.createElement('div');
            webTopRight.className = 'spider-web top-right';
            webTopRight.innerHTML = webTopLeft.innerHTML;

            websContainer.appendChild(webTopLeft);
            websContainer.appendChild(webTopRight);
            document.body.appendChild(websContainer);

            websContainer.style.display = isVideoPlaying ? 'none' : 'block';
        }

        // L'araignée qui descend, et les citrouilles qui tombent.
        // Comme pour la neige, le JS ne fournit que du hasard et une
        // silhouette ; tout le rendu et le rythme vivent dans le CSS.
        if (!document.getElementById('halloween-spider')) {
            const spider = document.createElement('div');
            spider.id = 'halloween-spider';
            spider.setAttribute('aria-hidden', 'true');
            spider.innerHTML = `
                <div class="thread"></div>
                <svg viewBox="0 0 40 40" fill="none" stroke="#E8E2F5" stroke-width="1.6" stroke-linecap="round">
                    <ellipse cx="20" cy="23" rx="7" ry="8" fill="#0B0714" stroke="none"/>
                    <circle cx="20" cy="14" r="4.2" fill="#0B0714" stroke="none"/>
                    <circle cx="18.3" cy="13.4" r="1" fill="#F58720" stroke="none"/>
                    <circle cx="21.7" cy="13.4" r="1" fill="#F58720" stroke="none"/>
                    <path d="M13 18 L5 13 M13 22 L3 21 M13 26 L5 31 M13 29 L8 36"/>
                    <path d="M27 18 L35 13 M27 22 L37 21 M27 26 L35 31 M27 29 L32 36"/>
                </svg>`;
            document.body.appendChild(spider);
            spider.style.display = isVideoPlaying ? 'none' : 'flex';
        }

        if (!document.getElementById('pumpkins-container')) {
            const pumpkins = document.createElement('div');
            pumpkins.id = 'pumpkins-container';
            pumpkins.className = 'pumpkins';
            pumpkins.setAttribute('aria-hidden', 'true');

            for (let i = 0; i < 8; i++) {
                const p = document.createElement('div');
                p.className = 'pumpkin';
                p.style.setProperty('--x', Math.random() * 100 + '%');
                p.style.setProperty('--drift', (Math.random() * 100 - 50) + 'px');
                p.style.setProperty('--delay', -Math.random() * 30 + 's');
                p.style.setProperty('--duration', (Math.random() * 14 + 22) + 's');
                p.style.setProperty('--scale', (Math.random() * 0.6 + 0.7).toFixed(2));
                p.innerHTML = `
                    <svg viewBox="0 0 24 24" fill="none">
                        <path d="M12 5c-.6-1.6.3-2.6 1.6-3" stroke="#4FB870" stroke-width="1.6" stroke-linecap="round"/>
                        <ellipse cx="12" cy="14" rx="8" ry="7" fill="#F58720"/>
                        <ellipse cx="8.5" cy="14" rx="3" ry="7" fill="#D9721A" opacity=".55"/>
                        <ellipse cx="15.5" cy="14" rx="3" ry="7" fill="#D9721A" opacity=".55"/>
                        <path d="M9.4 12.4l1.5 1.8-2.6.2zM14.6 12.4l-1.5 1.8 2.6.2zM9 16.6h6l-1.2 1.4h-3.6z" fill="#2A1204"/>
                    </svg>`;
                pumpkins.appendChild(p);
            }

            document.body.appendChild(pumpkins);
            pumpkins.style.display = isVideoPlaying ? 'none' : 'block';
        }

        // Ajouter la brume
        if (!document.getElementById('halloween-fog')) {
            const fog = document.createElement('div');
            fog.id = 'halloween-fog';
            fog.className = 'fog';
            fog.setAttribute('aria-hidden', 'true');
            document.body.appendChild(fog);

            fog.style.display = isVideoPlaying ? 'none' : 'block';
        }
    };

    const removeHalloweenTheme = () => {
        const css = document.getElementById('halloween-theme-css');
        if (css) css.remove();

        const batsContainer = document.getElementById('bats-container');
        if (batsContainer) batsContainer.remove();

        const webs = document.getElementById('spider-webs');
        if (webs) webs.remove();

        const spider = document.getElementById('halloween-spider');
        if (spider) spider.remove();

        const pumpkins = document.getElementById('pumpkins-container');
        if (pumpkins) pumpkins.remove();

        const fog = document.getElementById('halloween-fog');
        if (fog) fog.remove();
    };

    /*
     * L'application n'attend plus la réponse de `/theme` pour s'afficher.
     * Auparavant, tout le rendu était retenu derrière un écran noir non
     * stylé : un backend lent ou injoignable retardait l'affichage complet
     * pour une décoration saisonnière facultative. Le thème « default »
     * s'applique immédiatement, un éventuel thème saisonnier arrive après.
     */
    return (
        <ThemeContext.Provider value={{ theme, setTheme, fetchTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;